import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { dentalPrices, surgeryPrices, hairTransplantPrices } from '@/mocks/pricingLists';

const SUPPORTED_LANGS = [
  { code: 'de', label: 'DE', flag: '🇩🇪' },
  { code: 'en', label: 'EN', flag: '🇬🇧' },
  { code: 'it', label: 'IT', flag: '🇮🇹' },
  { code: 'es', label: 'ES', flag: '🇪🇸' },
  { code: 'fr', label: 'FR', flag: '🇫🇷' },
] as const;

type LangCode = (typeof SUPPORTED_LANGS)[number]['code'];

interface TreatmentItem {
  name: string;
  price: string;
  stay?: string;
}

type PricingCategory = 'dental' | 'surgery' | 'hair';

interface PricingDataByLang {
  [lang: string]: TreatmentItem[];
}

interface AllPricingData {
  dental: PricingDataByLang;
  surgery: PricingDataByLang;
  hair: PricingDataByLang;
}

const STORAGE_KEY = 'esteva_admin_pricing_list';

const CATEGORY_CONFIG: Record<PricingCategory, { label: string; labelKey: string; icon: string; color: string }> = {
  dental: { label: 'Zahnmedizin', labelKey: 'adm2.pe_cat_dental', icon: 'ri-tooth-line', color: 'primary' },
  surgery: { label: 'Plastische Chirurgie', labelKey: 'adm2.pe_cat_surgery', icon: 'ri-surgical-mask-line', color: 'secondary' },
  hair: { label: 'Haartransplantation', labelKey: 'adm2.pe_cat_hair', icon: 'ri-user-voice-line', color: 'accent' },
};

function buildDefaultData(): AllPricingData {
  const result: AllPricingData = { dental: {}, surgery: {}, hair: {} };

  SUPPORTED_LANGS.forEach(({ code }) => {
    result.dental[code] = JSON.parse(JSON.stringify(
      (dentalPrices as Record<string, TreatmentItem[]>)[code] || dentalPrices.en
    ));
    result.surgery[code] = JSON.parse(JSON.stringify(
      (surgeryPrices as Record<string, TreatmentItem[]>)[code] || surgeryPrices.en
    ));
    result.hair[code] = JSON.parse(JSON.stringify(
      (hairTransplantPrices as Record<string, TreatmentItem[]>)[code] || hairTransplantPrices.en
    ));
  });

  return result;
}

function loadData(): AllPricingData {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      // Merge with defaults to ensure all languages exist
      const defaults = buildDefaultData();
      const merged = { ...defaults };
      (['dental', 'surgery', 'hair'] as PricingCategory[]).forEach((cat) => {
        SUPPORTED_LANGS.forEach(({ code }) => {
          if (parsed[cat]?.[code] && Array.isArray(parsed[cat][code])) {
            merged[cat][code] = parsed[cat][code];
          }
        });
      });
      return merged;
    } catch {
      return buildDefaultData();
    }
  }
  return buildDefaultData();
}

function getDefaultData(): AllPricingData {
  return buildDefaultData();
}

interface PricingEditorProps {
  onSave?: () => void;
}

export default function PricingEditor({ onSave }: PricingEditorProps) {
  const { t } = useTranslation();
  const [data, setData] = useState<AllPricingData>(loadData);
  const [activeCategory, setActiveCategory] = useState<PricingCategory>('dental');
  const [editingLang, setEditingLang] = useState<LangCode>('de');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const treatments = data[activeCategory][editingLang] || [];
  const defaultTreatments = getDefaultData()[activeCategory][editingLang] || [];

  const handleTreatmentChange = (index: number, field: keyof TreatmentItem, value: string) => {
    setData((prev) => {
      const next = JSON.parse(JSON.stringify(prev)) as AllPricingData;
      if (!next[activeCategory][editingLang]) {
        next[activeCategory][editingLang] = [];
      }
      next[activeCategory][editingLang][index] = {
        ...next[activeCategory][editingLang][index],
        [field]: value,
      };
      return next;
    });
  };

  const handleAddTreatment = () => {
    setData((prev) => {
      const next = JSON.parse(JSON.stringify(prev)) as AllPricingData;
      if (!next[activeCategory][editingLang]) {
        next[activeCategory][editingLang] = [];
      }
      next[activeCategory][editingLang].push({
        name: t('adm2.pe_new_treatment'),
        price: '0',
        stay: activeCategory === 'surgery' || activeCategory === 'hair' ? t('adm2.pe_default_stay') : undefined,
      });
      return next;
    });
  };

  const handleRemoveTreatment = (index: number) => {
    setData((prev) => {
      const next = JSON.parse(JSON.stringify(prev)) as AllPricingData;
      next[activeCategory][editingLang].splice(index, 1);
      return next;
    });
  };

  const handleMoveTreatment = (index: number, direction: 'up' | 'down') => {
    setData((prev) => {
      const next = JSON.parse(JSON.stringify(prev)) as AllPricingData;
      const arr = next[activeCategory][editingLang];
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= arr.length) return prev;
      const tmp = arr[index];
      arr[index] = arr[newIndex];
      arr[newIndex] = tmp;
      return next;
    });
  };

  const handleSave = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    setSaved(true);
    setError('');

    // Activity logging
    const logKey = 'esteva_admin_activity_log';
    const existing = localStorage.getItem(logKey);
    const log = existing ? JSON.parse(existing) : [];
    log.unshift({
      action: 'pricing_updated',
      page: 'pricing',
      pageLabel: `Preisliste — ${CATEGORY_CONFIG[activeCategory].label}`,
      lang: editingLang,
      count: treatments.length,
      timestamp: new Date().toISOString(),
    });
    if (log.length > 20) log.pop();
    localStorage.setItem(logKey, JSON.stringify(log));
    window.dispatchEvent(new Event('admin-activity-updated'));
    window.dispatchEvent(new Event('admin-content-updated'));

    if (onSave) onSave();
    setTimeout(() => setSaved(false), 3000);
  };

  const handleReset = () => {
    const defaults = getDefaultData();
    setData((prev) => {
      const next = JSON.parse(JSON.stringify(prev)) as AllPricingData;
      next[activeCategory][editingLang] = JSON.parse(JSON.stringify(
        defaults[activeCategory][editingLang]
      ));
      return next;
    });
    setError(t('adm2.pe_category_reset', { category: t(CATEGORY_CONFIG[activeCategory].labelKey), lang: editingLang.toUpperCase() }));
    setTimeout(() => setError(''), 3000);
  };

  const handleResetAll = () => {
    if (window.confirm(t('adm2.pe_confirm_reset_all'))) {
      localStorage.removeItem(STORAGE_KEY);
      setData(getDefaultData());
      setError(t('adm2.pe_all_reset'));
      setTimeout(() => setError(''), 3000);
    }
  };

  const hasChanges = (): boolean => {
    const defaults = getDefaultData();
    return JSON.stringify(data) !== JSON.stringify(defaults);
  };

  const isItemChanged = (index: number): boolean => {
    const item = treatments[index];
    const def = defaultTreatments[index];
    if (!def) return true; // new item
    return item.name !== def.name || item.price !== def.price || item.stay !== def.stay;
  };

  const categoryColorClasses: Record<string, { tab: string; badge: string; addBtn: string; headerBg: string }> = {
    primary: { tab: 'border-primary-400 text-primary-700', badge: 'bg-primary-100 text-primary-700', addBtn: 'bg-primary-500 hover:bg-primary-600', headerBg: 'bg-primary-50' },
    secondary: { tab: 'border-secondary-400 text-secondary-700', badge: 'bg-secondary-100 text-secondary-700', addBtn: 'bg-secondary-500 hover:bg-secondary-600', headerBg: 'bg-secondary-50' },
    accent: { tab: 'border-accent-400 text-accent-700', badge: 'bg-accent-100 text-accent-700', addBtn: 'bg-accent-500 hover:bg-accent-600', headerBg: 'bg-accent-50' },
  };

  const color = categoryColorClasses[CATEGORY_CONFIG[activeCategory].color];

  return (
    <div className="space-y-6">
      {/* Category Tabs */}
      <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 w-fit">
        {(Object.keys(CATEGORY_CONFIG) as PricingCategory[]).map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-label tracking-[0.06em] transition-all cursor-pointer whitespace-nowrap ${
              activeCategory === cat
                ? `bg-background-50 border shadow-sm text-foreground-900 font-semibold`
                : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            <i className={`${CATEGORY_CONFIG[cat].icon} text-xs`}></i>
            {t(CATEGORY_CONFIG[cat].labelKey)}
            <span className="text-[10px] text-foreground-400 font-body">
              ({data[cat][editingLang]?.length || 0})
            </span>
          </button>
        ))}
      </div>

      {/* Language Tabs */}
      <div className="flex items-center gap-1 flex-wrap">
        {SUPPORTED_LANGS.map((lang) => (
          <button
            key={lang.code}
            onClick={() => setEditingLang(lang.code)}
            className={`px-4 py-1.5 rounded-full text-xs font-label tracking-[0.06em] transition-all cursor-pointer whitespace-nowrap ${
              editingLang === lang.code
                ? 'bg-foreground-900 text-background-50'
                : 'text-foreground-500 hover:text-foreground-700 hover:bg-background-100'
            }`}
          >
            {lang.flag} {lang.label}
          </button>
        ))}
      </div>

      {/* Info Banner */}
      <div className={`rounded-xl border p-4 ${color.headerBg} border-background-200/70`}>
        <div className="flex items-start gap-3">
          <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${color.badge} flex-shrink-0 mt-0.5`}>
            <i className={`${CATEGORY_CONFIG[activeCategory].icon} text-sm`}></i>
          </div>
          <div>
            <p className="text-xs font-label font-semibold text-foreground-900">
              {t(CATEGORY_CONFIG[activeCategory].labelKey)} — {SUPPORTED_LANGS.find((l) => l.code === editingLang)?.label}
            </p>
            <p className="text-[11px] text-foreground-500 font-body mt-0.5">
              {t('adm2.pe_treatments_count', { count: treatments.length })} · {t('adm2.pe_edit_hint')}
            </p>
          </div>
        </div>
      </div>

      {/* Saved / Error messages */}
      {saved && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-2">
          <i className="ri-check-line text-green-600 text-sm"></i>
          <span className="text-xs text-green-700 font-body">{t('adm2.pe_saved_lang', { lang: editingLang.toUpperCase() })}</span>
        </div>
      )}
      {error && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-2">
          <i className="ri-information-line text-amber-600 text-sm"></i>
          <span className="text-xs text-amber-700 font-body">{error}</span>
        </div>
      )}

      {/* Treatment List */}
      <div className="space-y-2">
        {treatments.map((item, index) => {
          const changed = isItemChanged(index);
          return (
            <div
              key={index}
              className={`group p-4 rounded-xl border transition-all ${
                changed
                  ? 'bg-yellow-50/40 border-yellow-200'
                  : 'bg-background-50 border-background-200/70 hover:border-background-300'
              }`}
            >
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
                {/* Name */}
                <div className="md:col-span-5">
                  <label className="text-[10px] tracking-[0.1em] uppercase text-foreground-400 font-label">{t('adm2.pe_treatment_name')}</label>
                  <input
                    type="text"
                    value={item.name}
                    onChange={(e) => handleTreatmentChange(index, 'name', e.target.value)}
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-100 transition-all"
                  />
                </div>

                {/* Price */}
                <div className="md:col-span-3">
                  <label className="text-[10px] tracking-[0.1em] uppercase text-foreground-400 font-label">{t('adm2.pe_price_eur')}</label>
                  <input
                    type="text"
                    value={item.price}
                    onChange={(e) => handleTreatmentChange(index, 'price', e.target.value)}
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-mono focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-100 transition-all"
                    placeholder={t('adm2.pe_price_ph')}
                  />
                </div>

                {/* Stay (optional) */}
                <div className="md:col-span-3">
                  <label className="text-[10px] tracking-[0.1em] uppercase text-foreground-400 font-label">
                    {t('adm2.pe_stay')} <span className="text-foreground-300 font-normal">({t('adm2.optional')})</span>
                  </label>
                  <input
                    type="text"
                    value={item.stay || ''}
                    onChange={(e) => handleTreatmentChange(index, 'stay', e.target.value)}
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-xs text-foreground-900 font-body focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-100 transition-all"
                    placeholder={t('adm2.pe_stay_ph')}
                  />
                </div>

                {/* Actions */}
                <div className="md:col-span-1 flex md:flex-col items-center md:items-end gap-1 mt-2 md:mt-5">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleMoveTreatment(index, 'up')}
                      disabled={index === 0}
                      className="w-7 h-7 flex items-center justify-center rounded-md bg-background-100 hover:bg-background-200 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                      title={t('adm2.move_up')}
                    >
                      <i className="ri-arrow-up-s-line text-xs"></i>
                    </button>
                    <button
                      onClick={() => handleMoveTreatment(index, 'down')}
                      disabled={index === treatments.length - 1}
                      className="w-7 h-7 flex items-center justify-center rounded-md bg-background-100 hover:bg-background-200 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                      title={t('adm2.move_down')}
                    >
                      <i className="ri-arrow-down-s-line text-xs"></i>
                    </button>
                    <button
                      onClick={() => handleRemoveTreatment(index)}
                      className="w-7 h-7 flex items-center justify-center rounded-md bg-red-50 hover:bg-red-100 text-red-400 hover:text-red-600 transition-colors cursor-pointer"
                      title={t('adm2.remove')}
                    >
                      <i className="ri-delete-bin-line text-xs"></i>
                    </button>
                  </div>
                  {changed && (
                    <span className="text-[9px] text-yellow-600 font-label px-1.5 py-0.5 rounded-full bg-yellow-100">
                      {t('adm2.changed')}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {treatments.length === 0 && (
          <div className="text-center py-16 rounded-xl border border-dashed border-background-300 bg-background-50">
            <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-price-tag-3-line text-2xl text-foreground-300"></i>
            </div>
            <p className="mt-3 text-sm text-foreground-400 font-body">{t('adm2.pe_no_treatments_lang')}</p>
            <p className="text-xs text-foreground-400 font-body mt-1">
              {t('adm2.pe_no_treatments_hint')}
            </p>
          </div>
        )}
      </div>

      {/* Action bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-4 border-t border-background-200/70">
        <button
          onClick={handleAddTreatment}
          className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-white text-xs font-label hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap ${color.addBtn}`}
        >
          <i className="ri-add-line text-sm"></i>
          {t('adm2.pe_add_treatment')}
        </button>

        <div className="flex items-center gap-2 ml-0 sm:ml-auto">
          <button
            onClick={handleResetAll}
            className="px-4 py-2.5 rounded-lg border border-red-200 text-red-600 text-xs font-label hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            {t('adm2.reset_all')}
          </button>
          <button
            onClick={handleReset}
            className="px-4 py-2.5 rounded-lg border border-background-200 text-foreground-500 text-xs font-label hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            {t('adm2.reset_lang', { lang: editingLang.toUpperCase() })}
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges()}
            className={`px-6 py-2.5 rounded-lg text-white text-xs font-label transition-all cursor-pointer whitespace-nowrap ${
              hasChanges()
                ? 'bg-primary-500 hover:bg-primary-600'
                : 'bg-foreground-200 cursor-not-allowed'
            }`}
          >
            {t('adm2.save')}
          </button>
        </div>
      </div>

      {/* Stats footer */}
      <div className="flex flex-wrap items-center gap-4 text-[10px] text-foreground-400 font-body pt-2">
        <span>{t('adm2.pe_total_treatments', { count: Object.values(data).reduce((sum, cat) => sum + (cat[editingLang]?.length || 0), 0), lang: editingLang.toUpperCase() })}</span>
        <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
        <span>{data.dental[editingLang]?.length || 0} {t('adm2.pe_short_dental')}</span>
        <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
        <span>{data.surgery[editingLang]?.length || 0} {t('adm2.pe_short_surgery')}</span>
        <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
        <span>{data.hair[editingLang]?.length || 0} {t('adm2.pe_short_hair')}</span>
      </div>
    </div>
  );
}